import React from 'react';
import { useWizard } from '@app/app/_components/Wizard/useWizard';
import { Wizard, WizardStep } from '@app/app/_components/Wizard/Wizard';
import { GatewayRedirectStep } from '@app/app/esales/transport/_sections/steps/GatewayRedirectStep';
import { Client } from '@app/domain/model/Client';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { FormState } from '@app/lib/form/contracts/FormState';
import { useBeforeUnload } from '@app/lib/use-before-unload/useBeforeUnload';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormState } from 'react-dom';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { ProductDataTerritories } from '@app/domain/model/eshop/transport/ProductDataTerritories';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';
import { scrollToTop } from '@app/lib/scroll-to-top/scrollToTop';
import { ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { ensureString } from '@app/lib/util/ensureString';
import { PurchaseTransportInsuranceSteps } from '@app/service/eshop/transport/schemas/PurchaseTransportInsuranceSteps';
import {
    createEnhancedFormAction,
    parsePersonalCode,
    validateCompanyCodeData,
} from '@app/app/esales/transport/_sections/TransportWizardSection/utils/transportWizardUtils';
import { usePriceCalculation } from '@app/app/esales/transport/_sections/setups/usePriceCalculation';
import { useOwnerFormFields } from '@app/app/esales/transport/_sections/setups/useOwnerFormFields';
import { useCriterionSubgroups } from '@app/app/esales/transport/_sections/setups/useCriterionSubgroups';
import { useTransportWizardComputedValues } from '@app/app/esales/transport/_sections/TransportWizardSection/hooks/useTransportWizardComputedValues';
import { useTransportWizardCallbacks } from '@app/app/esales/transport/_sections/TransportWizardSection/hooks/useTransportWizardCallbacks';
import { buildSteps } from '@app/app/esales/transport/_sections/TransportWizardSection/helpers/buildSteps';
import { TransportHiddenInputs } from '@app/app/esales/transport/_sections/TransportWizardSection/TransportHiddenInputs';
import { TransportWizardFooter } from '@app/app/esales/transport/_sections/TransportWizardSection/TransportWizardFooter';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';
import { PurchaseTransportInsuranceOfferStep } from '@app/service/eshop/transport/schemas/PurchaseTransportInsuranceOfferStep';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { useFontSize } from '@app/app/_context/AccessibilityContext/useFontSize';
import { FontSize } from '@app/domain/model/FontSize';
import { twJoin, twMerge } from 'tailwind-merge';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { useEffectOnce } from '@app/lib/use-change-effect/useEffectOnce';
import { clearSessionId } from '@app/infrastructure/eshop/clearSessionId';
import { trackProposalCalculated } from '@app/infrastructure/gtm/transport/trackProposalCalculated';
import { useTransportGtmTracking } from '@app/app/esales/transport/_sections/TransportWizardSection/hooks/useTransportGtmTracking';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    requestResult: TransportInsuranceRequestResult;
    onLeave: () => void;
    authState: AuthState;
    clients: Client[];
    mtplPurposes: Nullable<ProductDataPurpose>;
    cascoLeasings: ProductDataLeasing[];
    mtplTerritories: Nullable<ProductDataTerritories>;
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    initialPurchaseResult?: Nullable<PurchaseTransportInsuranceResult>;
    initialStep?: Nullable<string>;
}

export function TransportWizardSection({
    requestResult,
    onLeave,
    authState,
    clients,
    mtplPurposes,
    cascoLeasings,
    mtplTerritories,
    swedbankLogoSrc,
    sebLogoSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    initialPurchaseResult,
    initialStep,
}: Props) {
    const t = useTranslations();
    const fontSize = useFontSize();
    const formRef = useRef<HTMLFormElement>(null);
    const setCurrentStepKeyRef = useRef<((key: string) => void) | null>(null);
    const { settings } = useAppParameters();

    const [finished, setFinished] = useState(false);
    const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<Nullable<string>>(
        initialPurchaseResult?.formData.paymentMethodType ?? null,
    );
    const [discountCodeData, setDiscountCodeData] = useState<DiscountCodeData>({
        discountCode: initialPurchaseResult?.formData.discountCode ?? '',
        isInvalid: false,
        isSuccess: !!(
            initialPurchaseResult?.formData.discountCodeEcaPap || initialPurchaseResult?.formData.discountCodeCasco
        ),
        validEcaPapCode: initialPurchaseResult?.formData.discountCodeEcaPap,
        validCascoCode: initialPurchaseResult?.formData.discountCodeCasco,
    });
    const [cascoStartDate, setCascoStartDate] = useState(initialPurchaseResult?.formData.cascoStartDate ?? '');
    const [mtplStartDate, setMtplStartDate] = useState(initialPurchaseResult?.formData.mtplStartDate ?? '');
    const [insuranceDuration, setInsuranceDuration] = useState(initialPurchaseResult?.formData.insuranceDuration ?? '');
    const [isCalculatingPremiums, setIsCalculatingPremiums] = useState(false);
    const [loaderText, setLoaderText] = useState('');
    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);
    const [clientValidationErrorMessage, setClientValidationErrorMessage] = useState<Nullable<string>>(null);

    const isReinitializedSession = initialPurchaseResult != null && initialStep != null;

    const personalCodeData = parsePersonalCode(requestResult.personalCode);
    const isCompany = validateCompanyCodeData(requestResult.personalCode).isValid;

    const [currentPremiums, setCurrentPremiums] = useState<TransportPremiumsResponse[]>(
        initialPurchaseResult?.premiums ?? [],
    );

    const initialRidersCodes = useMemo(
        () => initialPurchaseResult?.formData.ridersTypeCodes?.map(rider => rider.code ?? '') ?? [],
        [initialPurchaseResult?.formData.ridersTypeCodes],
    );

    const {
        priceState,
        handleInsuranceChange,
        handleCascoPremiumChange,
        handleRidersChange,
        loadAvailableRiders,
        loadAvailableRidersWithParams,
        insurancePackage,
        updateSelectedPremiums,
        initializeFromSession,
    } = usePriceCalculation(
        currentPremiums,
        insuranceDuration,
        requestResult.vehicleInformation.typeCode ?? '',
        initialPurchaseResult?.formData.insurancePackage,
        initialRidersCodes,
    );

    useChangeEffect(() => {
        updateSelectedPremiums();
    }, currentPremiums);

    const enhancedFormAction = useMemo(
        () =>
            createEnhancedFormAction(
                requestResult,
                discountCodeData,
                personalCodeData,
                priceState,
                isReinitializedSession,
            ),
        [requestResult, discountCodeData, personalCodeData, priceState, isReinitializedSession],
    );

    const initialFormState = useMemo<FormState<PurchaseTransportInsuranceResult>>(() => {
        if (initialPurchaseResult) {
            return {
                status: FormStatus.Pending,
                data: initialPurchaseResult,
            };
        }
        return { status: FormStatus.Pending };
    }, [initialPurchaseResult]);

    const [formState, formAction] = useFormState(enhancedFormAction, initialFormState);

    const ownerFormFields = useOwnerFormFields(requestResult, isCompany);
    const { findOption } = useCriterionSubgroups(requestResult.cascoCriterions ?? []);

    const { ownerAge, experienceOtherDrivers, territory, selectedPurpose, isCascoPresent } =
        useTransportWizardComputedValues(formState, mtplPurposes, mtplTerritories, findOption);

    const mergedFormState = useMemo(() => {
        if (!clientValidationErrors) {
            return formState;
        }

        return {
            ...formState,
            violations: {
                ...formState.violations,
                ...clientValidationErrors,
            },
        };
    }, [formState, clientValidationErrors]);

    const [premiumOptionsAvailable, setPremiumOptionsAvailable] = useState(true);

    const handlePremiumOptionsChange = useCallback((available: boolean) => {
        setPremiumOptionsAvailable(available);
    }, []);

    const { trackStepCompletion } = useTransportGtmTracking({
        selectedCascoPremium: priceState.selectedCascoPremium,
        selectedMtplPremium: priceState.selectedMtplPremium,
        selectedRiders: priceState.selectedRiders,
        insurancePackage,
        availableRiders: priceState.availableRiders,
    });

    const {
        handleDiscountCode,
        handleApplyDiscountClick,
        handlePaymentMethodTypeChange,
        onSuccessfulLogin,
        navigateToStep,
        handlePaymentFrequencyChange,
    } = useTransportWizardCallbacks(
        discountCodeData,
        setDiscountCodeData,
        requestResult,
        setSelectedPaymentMethodType,
        setCurrentStepKeyRef,
        formState,
        insurancePackage as PremiumType,
        setIsCalculatingPremiums,
        setLoaderText,
        setCurrentPremiums,
        t,
    );

    const steps = useMemo<WizardStep[]>(
        () =>
            buildSteps({
                t,
                requestResult,
                formState: mergedFormState,
                handleDiscountCode,
                handleApplyDiscountClick,
                discountCodeData,
                mtplPurposes,
                setInsuranceDuration,
                isCascoPresent,
                isCompany,
                personalCodeData,
                cascoLeasings,
                mtplTerritories,
                handleInsuranceChange,
                insurancePackage: insurancePackage as PremiumType,
                currentPremiums,
                setCurrentPremiums,
                handlePremiumOptionsChange,
                handleCascoPremiumChange,
                priceState,
                handleRidersCheckboxChange: handleRidersChange,
                cascoStartDate,
                mtplStartDate,
                setCascoStartDate,
                setMtplStartDate,
                authState,
                swedbankLogoSrc,
                sebLogoSrc,
                clients,
                onSuccessfulLogin,
                ownerFormFields,
                selectedPurpose,
                ownerAge,
                experienceOtherDrivers,
                territory,
                navigateToStep,
                handlePaymentFrequencyChange,
                handlePaymentMethodTypeChange,
                selectedPaymentMethodType,
                isReinitializedSession,
                initialStep,
                settings,
            }),
        [
            t,
            requestResult,
            mergedFormState,
            handleDiscountCode,
            handleApplyDiscountClick,
            discountCodeData,
            mtplPurposes,
            isCascoPresent,
            isCompany,
            personalCodeData,
            cascoLeasings,
            mtplTerritories,
            handleInsuranceChange,
            insurancePackage,
            currentPremiums,
            handlePremiumOptionsChange,
            handleCascoPremiumChange,
            priceState,
            handleRidersChange,
            cascoStartDate,
            mtplStartDate,
            authState,
            swedbankLogoSrc,
            sebLogoSrc,
            clients,
            onSuccessfulLogin,
            ownerFormFields,
            selectedPurpose,
            ownerAge,
            experienceOtherDrivers,
            territory,
            navigateToStep,
            handlePaymentFrequencyChange,
            handlePaymentMethodTypeChange,
            selectedPaymentMethodType,
            isReinitializedSession,
            initialStep,
            settings,
        ],
    );

    const {
        filteredSteps,
        currentSubStepIndex,
        currentStepIndex,
        currentStepKey,
        isFirstStep,
        isFinalStep,
        next,
        back,
        setCurrentStepKey,
    } = useWizard(steps);

    useEffectOnce(() => {
        if (initialPurchaseResult && initialStep) {
            const { loadedMtplPremium, loadedCascoPremium } = initializeFromSession(
                initialPurchaseResult.formData.insurancePackage ?? '',
                initialPurchaseResult.formData.mtplPolicyPackageCode ?? null,
                initialPurchaseResult.formData.cascoPolicyPackageCode ?? null,
                (initialPurchaseResult.formData.ridersTypeCodes ?? []).map(rider => rider.code ?? ''),
            );

            loadAvailableRidersWithParams(
                loadedCascoPremium,
                loadedMtplPremium,
                initialPurchaseResult.formData.insurancePackage ?? '',
                initialPurchaseResult.formData.insuranceDuration ?? '',
            );
        }

        if (initialStep) {
            setCurrentStepKey(initialStep);
        }
    });

    useChangeEffect(() => {
        if (currentStepKey !== 'step-1-3' && currentStepKey !== 'step-2-1' && currentStepKey !== 'step-2-2') {
            return;
        }

        if (formState.data?.premiums && formState.data.premiums.length > 0) {
            setCurrentPremiums(formState.data?.premiums);
        }
    }, [formState.data?.premiums]);

    useChangeEffect(() => {
        setCurrentStepKeyRef.current = setCurrentStepKey;
    }, [setCurrentStepKey]);

    useBeforeUnload(finished, t('general.areYouSureYouWantToLeave'));

    const handleBack = useCallback(() => {
        if (isFirstStep) {
            const shouldLeave = confirm(t('general.areYouSureYouWantToLeave'));

            if (shouldLeave) {
                return onLeave();
            }
        }

        formState.status = FormStatus.Pending;
        setClientValidationErrors(null);
        setClientValidationErrorMessage(null);
        back();
    }, [back, formState, isFirstStep, onLeave, t]);

    const handleSubmit = useCallback(async () => {
        setClientValidationErrors(null);
        setClientValidationErrorMessage(null);

        if (currentStepKey === 'step-1-3' || currentStepKey === 'step-2-7') {
            const formData = new FormData(formRef.current!);

            try {
                const stepSchema =
                    currentStepKey === 'step-1-3'
                        ? PurchaseTransportInsuranceSteps['step-1-3']
                        : PurchaseTransportInsuranceOfferStep;

                const formDataObject: Record<string, string | boolean | number> = {};

                if (currentStepKey === 'step-1-3') {
                    formDataObject.drivingExperience = ensureString(formData.get('drivingExperience'), '');
                    formDataObject.ownerAge = ensureString(formData.get('ownerAge'), '');
                    formDataObject.experienceOtherDrivers = ensureString(formData.get('experienceOtherDrivers'), '');
                    formDataObject.territory = ensureString(formData.get('territory'), '');
                } else if (currentStepKey === 'step-2-7') {
                    formDataObject.insurancePackage = ensureString(formData.get('insurancePackage'), '');
                    formDataObject.agreeToPrivacyPolicy = formData.get('agreeToPrivacyPolicy') === 'true';
                    formDataObject.agreeToMtplTerms = formData.get('agreeToMtplTerms') === 'true';
                    formDataObject.agreeToCascoTerms = formData.get('agreeToCascoTerms') === 'true';
                    formDataObject.paymentCount = parseInt(
                        ensureString(formData.get('paymentCount'), InsuranceDuration.OneMonth),
                    );
                }

                await stepSchema.validate(formDataObject, { abortEarly: false, context: { isCompany } });

                if (currentStepKey === 'step-1-3') {
                    setIsCalculatingPremiums(true);
                    setLoaderText(t('eshop.transport.calculatingPremiums'));
                }

                if (currentStepKey === 'step-2-7') {
                    setIsCalculatingPremiums(true);
                    setLoaderText(t('eshop.transport.preparingOffer'));
                }

                formRef.current?.requestSubmit();
            } catch (error) {
                if (error instanceof ValidationError) {
                    const violations = buildViolations(error, 'general.validationError');
                    setClientValidationErrors(violations.violations ?? null);
                    setClientValidationErrorMessage('general.validationError');
                    scrollToTop();
                }
            }
        } else {
            formRef.current?.requestSubmit();
        }
    }, [currentStepKey, t, isCompany]);

    useChangeEffect(async () => {
        if (formState.status === FormStatus.Success) {
            setIsCalculatingPremiums(false);
            setClientValidationErrors(null);
            setClientValidationErrorMessage(null);

            if (currentStepKey === 'step-2-7' && formState.data?.offers) {
                const mtplPrice =
                    priceState.selectedMtplPremium?.premiumAfterDiscount ??
                    priceState.selectedMtplPremium?.premium ??
                    0;
                const cascoPrice =
                    priceState.selectedCascoPremium?.premiumAfterDiscount ??
                    priceState.selectedCascoPremium?.premium ??
                    0;
                const ridersPrice = priceState.selectedRiders.reduce(
                    (total, rider) => total + rider.premiums.premium,
                    0,
                );

                trackProposalCalculated({
                    insurancePackage: insurancePackage,
                    totalPrice: priceState.totalPrice,
                    mtplPrice: mtplPrice > 0 ? mtplPrice : null,
                    cascoPrice: cascoPrice > 0 ? cascoPrice : null,
                    ridersCount: priceState.selectedRiders.length,
                    ridersPrice: ridersPrice,
                    insuranceDuration: insuranceDuration,
                });
            }

            trackStepCompletion({ currentStepKey });

            if (isFinalStep) {
                await clearSessionId();
                setFinished(true);
            } else if (currentStepKey === 'step-2-2' || currentStepKey === 'step-2-1') {
                await loadAvailableRiders();
                next();
            } else {
                next();
            }
        }

        if (formState.status === FormStatus.Error) {
            scrollToTop();
        }

        setIsCalculatingPremiums(false);
    }, formState);

    useChangeEffect(() => {
        scrollToTop();
    }, currentStepKey);

    const getStepMarginClass = useCallback(() => {
        const stepMargins: Record<string, string> = {
            'step-1-1': '2xl:mb-[850px]',
            'step-1-2': '2xl:mb-[600px]',
            'step-1-3': '2xl:mb-[500px]',
            'step-2-1': '2xl:mb-[350px]',
            'step-2-2': '2xl:mb-[500px]',
            'step-2-4': '2xl:mb-[915px]',
            'step-2-5': '2xl:mb-[915px]',
            'step-2-6': '2xl:mb-[350px]',
            'step-3-1': '2xl:mb-[850px]',
            'step-3-2': '2xl:mb-[850px]',
            'step-3-3': '2xl:mb-[350px]',
        };

        if (currentStepKey === 'step-2-3' && priceState.availableRiders.length <= 2) {
            return '2xl:mb-[950px]';
        }

        return stepMargins[currentStepKey] || '';
    }, [currentStepKey, priceState.availableRiders.length]);

    if (finished) {
        return <GatewayRedirectStep checkoutResult={formState.data?.checkoutResult} />;
    }

    return (
        <>
            <GlobalLoader isVisible={isCalculatingPremiums} text={loaderText} />
            <form ref={formRef} action={formAction}>
                {!isFinalStep && <input type="hidden" name="step" value={currentStepKey} />}
                <input type="hidden" name="isCompany" value={isCompany.toString()} />

                {!requestResult.showVehicleModelStep && <TransportHiddenInputs requestResult={requestResult} />}

                <Wizard
                    steps={filteredSteps}
                    currentStepIndex={currentStepIndex}
                    currentSubStepIndex={currentSubStepIndex}
                    onSubmit={handleSubmit}
                    onBack={handleBack}
                    isPercentageStepper
                    maxProgressPercentage={95}
                    isLoading={priceState.isLoadingRiders}
                    contentFooter={
                        <div className={currentStepKey !== 'step-2-5' ? 'mb-12' : ''}>
                            <EsalesTransportFooter stakeholderLogoSrc={stakeholderLogoSrc} vigLogoSrc={vigLogoSrc} />
                        </div>
                    }
                    outerClassName="min-h-screen"
                    className={twMerge('mb-[300px] flex-1', getStepMarginClass())}
                    noFooter={currentStepKey === 'step-2-5'}
                    submitButtonLabel={
                        selectedPaymentMethodType != null && isFinalStep ? t('eshop.general.payLabel') : undefined
                    }
                    buttonClassName={twJoin(
                        fontSize !== FontSize.Normal && fontSize !== FontSize.Large && 'max-md:px-6',
                    )}
                    error={
                        clientValidationErrorMessage
                            ? t(clientValidationErrorMessage)
                            : formState.status === FormStatus.Error
                              ? t(formState.message)
                              : undefined
                    }
                    disabledButton={currentStepKey === 'step-2-1' && !premiumOptionsAvailable}
                    footer={
                        <TransportWizardFooter
                            formState={formState}
                            totalPrice={priceState.totalPrice}
                            currentStepKey={currentStepKey}
                        />
                    }
                />
            </form>
        </>
    );
}
